import { useState, type FormEvent } from 'react';
import type { Locale, SessionInfo } from '@avh/shared';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Feedback';
import { TextField } from '@/components/ui/Field';
import { apiRequest, errorMessage } from '@/lib/api';

export interface OtpLoginTexts {
  email: string;
  sendCode: string;
  codeSent: (email: string) => string;
  code: string;
  verify: string;
  otherEmail: string;
}

interface OtpLoginProps {
  texts: OtpLoginTexts;
  locale: Locale;
  onSuccess: (session: SessionInfo) => void;
}

type Step = { kind: 'email' } | { kind: 'code'; email: string };

/** Anmeldung per Einmal-Code aus der E-Mail – kein Passwort nötig. */
export function OtpLogin({ texts, locale, onSuccess }: OtpLoginProps) {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiRequest('/auth/request-code', { method: 'POST', body: { email, locale } });
      setStep({ kind: 'code', email: email.trim().toLowerCase() });
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.kind !== 'code') {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSuccess(await apiRequest<SessionInfo>('/auth/verify', { method: 'POST', body: { email: step.email, code } }));
    } catch (verifyError) {
      setError(errorMessage(verifyError));
      setBusy(false);
    }
  }

  if (step.kind === 'email') {
    return (
      <form onSubmit={requestCode} className="flex flex-col gap-4">
        <TextField
          label={texts.email}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Button type="submit" disabled={busy}>
          {texts.sendCode}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="flex flex-col gap-4">
      <Alert tone="info">{texts.codeSent(step.email)}</Alert>
      <TextField
        label={texts.code}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        required
        autoFocus
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
        className="text-center font-mono text-2xl tracking-[0.5em]"
      />
      {error ? <Alert tone="error">{error}</Alert> : null}
      <Button type="submit" disabled={busy || code.length !== 6}>
        {texts.verify}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setStep({ kind: 'email' });
          setCode('');
          setError(null);
        }}
      >
        {texts.otherEmail}
      </Button>
    </form>
  );
}
